// Shared contracts and pure logic for the reports feature (Summary, Detailed, Weekly).
import { z } from 'zod';

/* ------------------------------------------------------------------------------------------------
 * Calendar-date helpers. Dates are ISO `YYYY-MM-DD` strings with no time zone: callers decide
 * which zone "today" is in. Arithmetic is done on UTC midnights so DST never shifts a day.
 * --------------------------------------------------------------------------------------------- */

export type DateRange = { start: string; end: string };

const DAY_MS = 86_400_000;
const toUtc = (d: string) => Date.parse(`${d}T00:00:00Z`);
const fromUtc = (ms: number) => new Date(ms).toISOString().slice(0, 10);

export function addDays(date: string, days: number): string {
  return fromUtc(toUtc(date) + days * DAY_MS);
}

/** Whole days from `a` to `b` (b - a). */
export function daysBetween(a: string, b: string): number {
  return Math.round((toUtc(b) - toUtc(a)) / DAY_MS);
}

/** 0 = Sunday … 6 = Saturday. */
export function dayOfWeek(date: string): number {
  return new Date(toUtc(date)).getUTCDay();
}

/** First day of the week containing `date`, for a week starting on `weekStart` (0 = Sunday). */
export function weekStartOf(date: string, weekStart: number): string {
  return addDays(date, -((dayOfWeek(date) - weekStart + 7) % 7));
}

function ymd(y: number, m: number, d: number) {
  return fromUtc(Date.UTC(y, m, d));
}
const parts = (date: string) => ({
  y: Number(date.slice(0, 4)),
  m: Number(date.slice(5, 7)) - 1,
  d: Number(date.slice(8, 10)),
});
const monthStart = (y: number, m: number) => ymd(y, m, 1);
const monthEnd = (y: number, m: number) => ymd(y, m + 1, 0);

export const DATE_PRESETS = [
  'today',
  'yesterday',
  'thisWeek',
  'lastWeek',
  'pastTwoWeeks',
  'thisMonth',
  'lastMonth',
  'thisYear',
  'lastYear',
] as const;
export type DatePreset = (typeof DATE_PRESETS)[number];

export const DATE_PRESET_LABELS: Record<DatePreset, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  thisWeek: 'This week',
  lastWeek: 'Last week',
  pastTwoWeeks: 'Past two weeks',
  thisMonth: 'This month',
  lastMonth: 'Last month',
  thisYear: 'This year',
  lastYear: 'Last year',
};

/** Clockify's date-picker presets. "Past two weeks" is last week plus this week. */
export function presetRange(preset: DatePreset, today: string, weekStart: number): DateRange {
  const { y, m } = parts(today);
  const week = weekStartOf(today, weekStart);
  switch (preset) {
    case 'today':
      return { start: today, end: today };
    case 'yesterday': {
      const d = addDays(today, -1);
      return { start: d, end: d };
    }
    case 'thisWeek':
      return { start: week, end: addDays(week, 6) };
    case 'lastWeek':
      return { start: addDays(week, -7), end: addDays(week, -1) };
    case 'pastTwoWeeks':
      return { start: addDays(week, -7), end: addDays(week, 6) };
    case 'thisMonth':
      return { start: monthStart(y, m), end: monthEnd(y, m) };
    case 'lastMonth':
      return { start: monthStart(y, m - 1), end: monthEnd(y, m - 1) };
    case 'thisYear':
      return { start: ymd(y, 0, 1), end: ymd(y, 11, 31) };
    case 'lastYear':
      return { start: ymd(y - 1, 0, 1), end: ymd(y - 1, 11, 31) };
  }
}

/** Which preset (if any) a range corresponds to today. */
export function matchPreset(range: DateRange, today: string, weekStart: number): DatePreset | null {
  for (const p of DATE_PRESETS) {
    const r = presetRange(p, today, weekStart);
    if (r.start === range.start && r.end === range.end) return p;
  }
  return null;
}

/**
 * The ‹ › arrows: ranges made of whole calendar months (a month, quarter or year) move by
 * their number of months; anything else moves by its own length in days.
 */
export function shiftRange(range: DateRange, direction: 1 | -1): DateRange {
  const s = parts(range.start);
  const e = parts(range.end);
  const wholeMonths = s.d === 1 && range.end === monthEnd(e.y, e.m);
  if (wholeMonths) {
    const months = (e.y - s.y) * 12 + (e.m - s.m) + 1;
    const offset = months * direction;
    return { start: monthStart(s.y, s.m + offset), end: monthEnd(e.y, e.m + offset) };
  }
  const len = daysBetween(range.start, range.end) + 1;
  return { start: addDays(range.start, len * direction), end: addDays(range.end, len * direction) };
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const monthDay = (date: string) => {
  const { m, d } = parts(date);
  return `${MONTHS[m]!.slice(0, 3)} ${d}`;
};

/**
 * Clockify's labels for date groups, from their ISO ids: `Mon, Mar 2, 2026` (date),
 * `Mar 2 – Mar 8, 2026` (week start id; both years when it spans New Year) and `March 2026`
 * (month id `2026-03`).
 */
export function dateGroupLabel(group: 'date' | 'week' | 'month', id: string): string {
  if (group === 'month') return `${MONTHS[Number(id.slice(5, 7)) - 1]} ${id.slice(0, 4)}`;
  const year = id.slice(0, 4);
  if (group === 'date') return `${WEEKDAYS[dayOfWeek(id)]}, ${monthDay(id)}, ${year}`;
  const end = addDays(id, 6);
  const endYear = end.slice(0, 4);
  return endYear === year
    ? `${monthDay(id)} – ${monthDay(end)}, ${year}`
    : `${monthDay(id)}, ${year} – ${monthDay(end)}, ${endYear}`;
}

export type DurationFormat = 'hms' | 'decimal';

/** `12:30:00` (Clockify's hh:mm:ss, hours unbounded) or `12.50` (decimal hours). */
export function formatDuration(sec: number, format: DurationFormat): string {
  if (format === 'decimal') return (sec / 3600).toFixed(2);
  const s = Math.max(0, Math.round(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(s % 60)}`;
}

/* ------------------------------------------------------------------------------------------------
 * Request contracts. Every schema accepts a parsed query string: repeated keys arrive as arrays,
 * single keys as strings, numbers and booleans as strings.
 * --------------------------------------------------------------------------------------------- */

/** Filter value meaning "Without client / project / task / tag". */
export const NONE_ID = 'none';

/** Clockify's "Group by" options, in its menu order. */
export const REPORT_GROUPS = [
  'project',
  'client',
  'task',
  'user',
  'tag',
  'date',
  'week',
  'month',
  'description',
] as const;
export type ReportGroup = (typeof REPORT_GROUPS)[number];

export const REPORT_GROUP_LABELS: Record<ReportGroup, string> = {
  project: 'Project',
  client: 'Client',
  task: 'Task',
  user: 'User',
  tag: 'Tag',
  date: 'Date',
  week: 'Week',
  month: 'Month',
  description: 'Description',
};

/** Label of the group holding entries with no value for a dimension. */
export const WITHOUT_LABELS: Record<ReportGroup, string> = {
  project: 'Without project',
  client: 'Without client',
  task: 'Without task',
  user: 'Without user',
  tag: 'Without tag',
  date: 'Without date',
  week: 'Without week',
  month: 'Without month',
  description: 'Without description',
};

export const BILLABLE_FILTERS = ['all', 'billable', 'nonbillable'] as const;
export type BillableFilter = (typeof BILLABLE_FILTERS)[number];

export const DETAILED_SORTS = ['date', 'user', 'project', 'duration'] as const;
export type DetailedSort = (typeof DETAILED_SORTS)[number];

export const WEEKLY_GROUPS = ['project', 'user'] as const;
export type WeeklyGroup = (typeof WEEKLY_GROUPS)[number];

export const DEFAULT_GROUP_BY: ReportGroup[] = ['project', 'description'];
export const MAX_SUMMARY_RANGE_DAYS = 5 * 366;
export const MAX_DETAILED_RANGE_DAYS = 366;
export const MAX_PAGE_SIZE = 200;

const asArray = (v: unknown) => (v === undefined || v === null || v === '' ? [] : Array.isArray(v) ? v : [v]);
const list = <T extends z.ZodType>(item: T) => z.preprocess(asArray, z.array(item).max(500));
const idOrNone = z.union([z.uuid(), z.literal(NONE_ID)]);
const bool = z.preprocess((v) => (v === 'true' ? true : v === 'false' ? false : v), z.boolean());

const filterShape = {
  /** Inclusive local dates in the viewer's time zone. */
  start: z.iso.date(),
  end: z.iso.date(),
  userIds: list(z.uuid()),
  clientIds: list(idOrNone),
  projectIds: list(idOrNone),
  taskIds: list(idOrNone),
  tagIds: list(idOrNone),
  billable: z.enum(BILLABLE_FILTERS).default('all'),
  /** Case-insensitive "contains". */
  description: z.string().trim().max(500).optional(),
  /** Only entries with an empty description (takes precedence over `description`). */
  withoutDescription: bool.default(false),
};

const groupByShape = z
  .preprocess(asArray, z.array(z.enum(REPORT_GROUPS)).min(1).max(3))
  .refine((g) => new Set(g).size === g.length, 'Each group can be used once')
  .default(DEFAULT_GROUP_BY);

type WithRange = { start: string; end: string };
function rangeRefine(maxDays: number) {
  return (f: WithRange, ctx: z.RefinementCtx) => {
    const span = daysBetween(f.start, f.end) + 1;
    if (span < 1) ctx.addIssue({ code: 'custom', path: ['end'], message: 'End date is before start date' });
    else if (span > maxDays)
      ctx.addIssue({ code: 'custom', path: ['end'], message: `Date range is limited to ${maxDays} days` });
  };
}

/** Summary report query (and the base filter every report understands). */
export const reportFilterSchema = z
  .object({ ...filterShape, groupBy: groupByShape })
  .superRefine(rangeRefine(MAX_SUMMARY_RANGE_DAYS));
export type ReportFilter = z.infer<typeof reportFilterSchema>;

export const detailedQuerySchema = z
  .object({
    ...filterShape,
    sort: z.enum(DETAILED_SORTS).default('date'),
    order: z.enum(['asc', 'desc']).default('desc'),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(50),
  })
  .superRefine(rangeRefine(MAX_DETAILED_RANGE_DAYS));
export type DetailedQuery = z.infer<typeof detailedQuerySchema>;

/** The Detailed report's filters alone (no order or paging), e.g. for bulk actions on them. */
export const detailedFilterSchema = z.object(filterShape).superRefine(rangeRefine(MAX_DETAILED_RANGE_DAYS));
export type DetailedFilter = z.infer<typeof detailedFilterSchema>;
export type DetailedFilterInput = z.input<typeof detailedFilterSchema>;

export const weeklyQuerySchema = z
  .object({ ...filterShape, groupBy: z.enum(WEEKLY_GROUPS).default('project') })
  .superRefine((f, ctx) => {
    if (daysBetween(f.start, f.end) !== 6)
      ctx.addIssue({ code: 'custom', path: ['end'], message: 'The weekly report covers exactly 7 days' });
  });
export type WeeklyQuery = z.infer<typeof weeklyQuerySchema>;

/* ------------------------------------------------------------------------------------------------
 * Response contracts. Money is in minor units of the tenant currency. All money — amounts, rates,
 * cost and profit — is null unless the viewer is an admin.
 * --------------------------------------------------------------------------------------------- */

export const reportTotalsSchema = z.object({
  durationSec: z.number().int(),
  billableDurationSec: z.number().int(),
  amountCents: z.number().int().nullable(),
  costCents: z.number().int().nullable(),
  profitCents: z.number().int().nullable(),
  entryCount: z.number().int(),
});
export type ReportTotals = z.infer<typeof reportTotalsSchema>;

export type GroupNode = {
  /** Entity id, date key (YYYY-MM-DD / YYYY-MM) or description text; null = "Without …". */
  id: string | null;
  label: string;
  color?: string | null;
  durationSec: number;
  billableDurationSec: number;
  amountCents: number | null;
  costCents: number | null;
  children: GroupNode[];
};
export const groupNodeSchema: z.ZodType<GroupNode> = z.lazy(() =>
  z.object({
    id: z.string().nullable(),
    label: z.string(),
    color: z.string().nullable().optional(),
    durationSec: z.number().int(),
    billableDurationSec: z.number().int(),
    amountCents: z.number().int().nullable(),
    costCents: z.number().int().nullable(),
    children: z.array(groupNodeSchema),
  }),
);

export const summaryReportSchema = z.object({
  totals: reportTotalsSchema,
  currency: z.string(),
  groupBy: z.array(z.enum(REPORT_GROUPS)),
  byDay: z.array(
    z.object({ date: z.string(), durationSec: z.number().int(), billableDurationSec: z.number().int() }),
  ),
  groups: z.array(groupNodeSchema),
});
export type SummaryReport = z.infer<typeof summaryReportSchema>;

const ref = z.object({ id: z.uuid(), name: z.string() });
export const detailedEntrySchema = z.object({
  id: z.uuid(),
  /** Local start date in the viewer's time zone. */
  date: z.string(),
  description: z.string(),
  user: ref,
  project: ref.extend({ color: z.string() }).nullable(),
  client: ref.nullable(),
  task: ref.nullable(),
  tags: z.array(ref),
  billable: z.boolean(),
  startAt: z.string(),
  endAt: z.string(),
  durationSec: z.number().int(),
  billRateCents: z.number().int().nullable(),
  amountCents: z.number().int().nullable(),
  costCents: z.number().int().nullable(),
});
export type DetailedEntry = z.infer<typeof detailedEntrySchema>;

export const detailedReportSchema = z.object({
  totals: reportTotalsSchema,
  currency: z.string(),
  entries: z.array(detailedEntrySchema),
  page: z.number().int(),
  pageSize: z.number().int(),
  totalEntries: z.number().int(),
});
export type DetailedReport = z.infer<typeof detailedReportSchema>;

export const weeklyReportSchema = z.object({
  totals: reportTotalsSchema,
  currency: z.string(),
  groupBy: z.enum(WEEKLY_GROUPS),
  days: z.array(z.string()).length(7),
  rows: z.array(
    z.object({
      id: z.string().nullable(),
      label: z.string(),
      color: z.string().nullable().optional(),
      byDay: z.array(z.number().int()).length(7),
      total: z.number().int(),
    }),
  ),
  totalsByDay: z.array(z.number().int()).length(7),
  total: z.number().int(),
});
export type WeeklyReport = z.infer<typeof weeklyReportSchema>;

/* ------------------------------------------------------------------------------------------------
 * Exports: GET /api/reports/{summary|detailed|weekly}/export?format=xlsx|pdf&<the report's query>
 * --------------------------------------------------------------------------------------------- */

export const REPORT_KINDS = ['summary', 'detailed', 'weekly'] as const;
export type ReportKind = (typeof REPORT_KINDS)[number];

export const EXPORT_FORMATS = ['xlsx', 'pdf'] as const;
export type ExportFormat = (typeof EXPORT_FORMATS)[number];
export const exportFormatSchema = z.object({ format: z.enum(EXPORT_FORMATS) });

/** Detailed exports are not paginated; beyond these many entries the range must be narrowed. */
export const DETAILED_EXPORT_MAX_ROWS = 100_000;
/** PDF layout is far costlier per row than a spreadsheet, so PDFs get a lower cap. */
export const DETAILED_PDF_MAX_ROWS = 10_000;
/** Summary exports: at most this many group rows (all levels counted) per file. */
export const SUMMARY_EXPORT_MAX_ROWS = 20_000;
export const SUMMARY_PDF_MAX_ROWS = 5_000;
